import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, BlockStack, InlineStack, Text, Button, Icon, Banner, Box } from '@shopify/polaris';
import { LockIcon } from '@shopify/polaris-icons';
import { useSubscriptionStatus } from '../hooks/useSubscription';
import type { Tier } from '../types';

const TIER_LABELS: Record<Tier, string> = {
  FREE: 'Free',
  STARTER: 'Starter',
  GROWTH: 'Growth',
  SCALE: 'Scale',
};

interface LockedFeatureCardProps {
  featureName: string;
  requiredTier: Tier;
  description?: string;
  source?: string;
}

/**
 * LockedFeatureCard — inline locked state for features unavailable on the current plan.
 * Shows a Card with a lock icon, plan requirement info, and an upgrade CTA.
 * Used in place of feature sections (AutoFixPreview, ProductSummarySection, etc.)
 * when the store's tier doesn't have access.
 */
export function LockedFeatureCard({
  featureName,
  requiredTier,
  description,
}: Readonly<LockedFeatureCardProps>): React.JSX.Element {
  const { data: status } = useSubscriptionStatus();
  const navigate = useNavigate();

  const currentTierLabel = TIER_LABELS[status?.tier ?? 'FREE'];
  const requiredTierLabel = TIER_LABELS[requiredTier];

  return (
    <Card>
        <BlockStack gap="300">
          <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
            <Box><Icon source={LockIcon} /></Box>
            <Text as="h2" variant="headingMd">{featureName}</Text>
          </InlineStack>

          <Banner tone="info">
            <Text as="p" variant="bodySm">
              {featureName} is available on the {requiredTierLabel} plan and above.
              You&apos;re currently on the {currentTierLabel} plan.
            </Text>
          </Banner>

          {description && (
            <Text as="p" variant="bodyMd" tone="subdued">{description}</Text>
          )}

          <InlineStack>
            <Button variant="primary" onClick={() => navigate('/billing')}>
              Upgrade to {requiredTierLabel}
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
  );
}
